import React, { PureComponent } from "react";
import { Card, Col, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import {
  filter,
  camelCase,
  upperFirst,
  toLower,
  startCase,
  size,
} from "lodash";
import { ScrollToFieldError } from "utils/form";
import {
  daysOfMonthOptions,
  MONTHLY,
  END_OF_MONTH,
  payrollPeriodsToEnglishMapping,
} from "utils/timeHelper";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { formatAmount, formatCurrency, formatValue } from "utils/number";
import { accountTypesToEnglishMapping } from "statics/accountTypes";

import Button from "components/Button";
import DatePicker from "react-datepicker";
import InfoTooltip from "components/InfoTooltip";
import Alert from "components/Alert";

import AccountBox from "../../../../components/transferWidget/AccountBox";

import * as yup from "yup";

const schema = yup.object({
  selectedAccountId: yup
    .string()
    .label("To Account")
    .required("Please select an account"),
  frequency: yup
    .string()
    .label("Payment Frequency")
    .required("Please select a payment frequency"),
  dayOne: yup
    .string()
    .label("Payment Date")
    .when("frequency", {
      is: (frequency: string) => frequency === MONTHLY,
      then: (s) => s.required("Please select a day of the month"),
      otherwise: (s) => s.nullable(),
    }),
  anchorPayDate: yup
    .date()
    .label("First Payment Date")
    .when("frequency", {
      is: (frequency: string) => frequency && frequency !== MONTHLY,
      then: (s) => s.required("Please select a start date"),
      otherwise: (s) => s.nullable(),
    }),
  customAmount: yup.number().when(["selectedCustom"], {
    is: true,
    then: (s) =>
      s
        .required("Please enter an amount")
        .min(0.01, "Amount must be greater than $0"),
    otherwise: (s) => s.nullable(),
  }),
});

const PAYCHECK_LIMITS: Record<string, { max: number; min: number }> = {
  MONTHLY: { max: 1000, min: 25 },
  TWICE_PER_MONTH: { max: 500, min: 13 },
  EVERY_OTHER_WEEK: { max: 500, min: 13 },
  WEEKLY: { max: 250, min: 6 },
};

interface RecommendedContribution {
  recommended?: number;
  other?: number[];
  isMaxContributionOption?: boolean;
}

interface ScheduleProp {
  frequency?: string;
  dayOne?: string;
  anchorPayDate?: string;
}

interface ContributionAccount {
  id?: string;
  accountType?: string;
}

interface OwnProps {
  // Schedule props
  schedule?: ScheduleProp;

  // Contribution props
  isSubmitting?: boolean;
  error?: string;
  bankAccountNumber?: string;
  currentContributionAmount?: number;

  // Per-frequency max props forwarded by RecurringContributionModal. Not read
  // here (the component derives limits from PAYCHECK_LIMITS), but accepted so
  // the parent's pass type-checks; values are `number | null | undefined`.
  weeklyMax?: number | null;
  monthlyMax?: number | null;
  everyOtherWeekMax?: number | null;

  everyOtherWeekRecommendations?: RecommendedContribution;

  // Callbacks
  onSubmit: (data: {
    schedule: {
      frequency: string;
      dayOne: number | undefined;
      anchorPayDate: string;
    };
    amount: number;
    isDailyCustom: boolean;
    accountId: string;
  }) => void;
  isEditing?: boolean;
  onClose?: () => void;

  // Account props
  fromBankName?: string;
  fromAccountName?: string;
  isFromBank?: boolean;
  fromLastFourOfAccount?: string;
  accounts?: ContributionAccount[];
  defaultAccountId?: string;

  // `${camelCase(frequency)}Recommendations` are read dynamically off props
  [key: `${string}Recommendations`]: RecommendedContribution | undefined;
}

type Props = WithApolloClient<OwnProps>;

interface State {
  selectedCustom: boolean;
  showCustomBox: boolean;
  customAmount: number | string;
  customAmountError: string;
  showConfirmation: boolean;
  selectedAccountId: string;
}

interface FormValues {
  selectedAccountId: string;
  frequency: string;
  dayOne: string;
  anchorPayDate: moment.Moment | null;
  selectedCustom: boolean;
  customAmount: number | string;
}

class ContributionSetup extends PureComponent<Props, State> {
  initialFormValues: FormValues;

  constructor(props: Props) {
    super(props);

    const initialAmount = props.currentContributionAmount || "";

    this.state = {
      selectedCustom: true,
      showCustomBox: true,
      customAmount: initialAmount,
      customAmountError: "",
      showConfirmation: false,
      selectedAccountId: this._getInitialSelectedAccountId(),
    };

    this.initialFormValues = {
      selectedAccountId: this._getInitialSelectedAccountId(),
      frequency: props.schedule?.frequency || "",
      dayOne: props.schedule?.dayOne ? props.schedule.dayOne.toString() : "",
      anchorPayDate: props.schedule?.anchorPayDate
        ? moment(props.schedule.anchorPayDate)
        : null,
      selectedCustom: true,
      customAmount: initialAmount,
    };
  }
  _getInitialSelectedAccountId = (): string => {
    const { defaultAccountId, accounts } = this.props;
    if (defaultAccountId) {
      return defaultAccountId;
    }
    if (accounts && accounts.length > 0) {
      // original returns accounts[0].id verbatim; `!` is erased at runtime
      return accounts[0].id!;
    }
    return "";
  };

  _showDayOne = (frequency: string) => {
    return frequency === MONTHLY;
  };

  parseDayString = (day: string) => {
    return day === END_OF_MONTH ? 31 : parseInt(day);
  };

  _getRecommendedContributions = (frequency: string) => {
    if (!frequency) return null;
    return this.props[`${camelCase(frequency)}Recommendations`];
  };

  _getContributionLimits = (frequency: string) => {
    if (!frequency) return { min: 0, max: 0 };

    const scheduleLimit = PAYCHECK_LIMITS[frequency];
    let min;

    if (this.props.isEditing) {
      min = 0;
    } else {
      min = scheduleLimit ? scheduleLimit.min : 0;
    }

    return {
      min,
      max: scheduleLimit ? scheduleLimit.max : 0,
    };
  };

  _setCustomAmount = (amt: number | string, frequency: string) => {
    let customAmountError = "";

    // Don't validate if amount is empty or 0
    if (!amt || amt === 0) {
      return {
        customAmount: amt.toString(),
        customAmountError: "",
      };
    }

    const contributionLimits = this._getContributionLimits(frequency);

    // amt is string | number; the original relies on JS coercion in these
    // comparisons — preserved verbatim.
    if (
      contributionLimits.min > 0 &&
      // @ts-expect-error amt is string | number; comparison coerces at runtime
      amt < contributionLimits.min
    ) {
      customAmountError = `Custom amount must be greater than or equal to ${formatCurrency(
        contributionLimits.min
      )}`;
    } else if (
      contributionLimits.max > 0 &&
      // @ts-expect-error amt is string | number; comparison coerces at runtime
      amt > contributionLimits.max
    ) {
      customAmountError = `Custom amount must be less than or equal to ${formatCurrency(
        contributionLimits.max
      )}`;
    }

    return {
      customAmount: formatAmount(amt),
      customAmountError,
    };
  };

  _getConfirmationMessage = (values: FormValues) => {
    const { frequency, anchorPayDate, dayOne } = values;
    const { customAmount } = this.state;

    let firstPaymentDate: moment.Moment | string | null = anchorPayDate;

    // Always ensure firstPaymentDate is a formatted string, not a Moment object
    if (firstPaymentDate && moment.isMoment(firstPaymentDate)) {
      firstPaymentDate = firstPaymentDate.format("MMM DD, YYYY");
    } else if (firstPaymentDate && typeof firstPaymentDate === "string") {
      // If it's already a string, make sure it's properly formatted
      firstPaymentDate = moment(firstPaymentDate).format("MMM DD, YYYY");
    }

    if (frequency === MONTHLY) {
      const dayOneValue = dayOne === "End of Month" ? 31 : parseInt(dayOne);

      // For monthly, we don't use anchorPayDate - we use today's date as reference
      const today = moment();

      // Create processing date for current month
      const processingDate = moment()
        .date(dayOneValue)
        .month(today.month())
        .year(today.year());

      // If today is after this month's processing date, move to next month
      if (today.isAfter(processingDate)) {
        processingDate.add(1, "month");
      }

      firstPaymentDate = processingDate.format("MMM DD, YYYY");
    }

    const fromAccount = upperFirst(toLower(this.props.fromBankName));
    const selectedAccount = this.props.accounts!.find(
      (a) => a.id === this.state.selectedAccountId
    );
    const toAccount = selectedAccount
      ? (accountTypesToEnglishMapping as Record<string, string>)[
          selectedAccount.accountType!
        ]
      : "";

    const contributionMetadata = [
      {
        label: "From",
        key: "fromAccount",
      },
      {
        label: "Transfer to",
        key: "toAccount",
      },
      {
        label: "Frequency",
        key: "frequency",
      },
      {
        label: "Start Date",
        key: "startDate",
      },
    ];

    const contributionData = [
      {
        fromAccount,
        toAccount,
        frequency: (payrollPeriodsToEnglishMapping as Record<string, string>)[
          frequency
        ],
        startDate: firstPaymentDate,
        amount: customAmount,
      },
    ];

    return (
      <div className="transfer-details">
        <>
          <div className="route">
            Scheduled transfer from {fromAccount} to {toAccount}
          </div>
          <div className="amount">{formatCurrency(customAmount)}</div>
          <div className="timing">Funds should arrive in 5 business days.</div>
        </>

        {contributionMetadata.map((meta, idx) => {
          const extraClass = idx === 0 ? "with-border" : "";
          let value: unknown = (contributionData[0] as Record<string, unknown>)[
            meta.key
          ];

          if (moment.isMoment(value)) {
            value = value.format("MMM DD, YYYY");
          }

          // meta here is { label, key }; formatValue reads only `.format`
          // (absent → returns value unchanged), so the shape is compatible
          const formattedValue = formatValue(
            meta as Parameters<typeof formatValue>[0],
            value
          );

          return (
            <div className={`detail-item ${extraClass}`} key={idx}>
              <span className="label">{startCase(toLower(meta.label))}</span>
              <span className="value">{formattedValue as React.ReactNode}</span>
            </div>
          );
        })}

        <div className="authorization">
          By clicking Submit, you authorize Icon Financial Services to initiate
          the scheduled contributions as detailed above. Future scheduled
          payments will continue until you modify or cancel them. Payments may
          be canceled up to 1 day before the scheduled transfer date.
        </div>
        {this.props.error && <Alert type="error" msg={this.props.error} />}
        <div className="submit-row btn-row">
          <Button
            btnLabel="go back"
            color="cancel"
            name="cancel"
            onClick={() => this.setState({ showConfirmation: false })}
          />
          <Button
            btnLabel="submit"
            name="submit"
            withArrow
            loading={this.props.isSubmitting}
            onClick={() => this._handleFinalSubmit(values)}
          />
        </div>
      </div>
    );
  };

  _handleFinalSubmit = (values: FormValues) => {
    const scheduleData = {
      frequency: values.frequency,
      dayOne:
        values.frequency === MONTHLY
          ? this.parseDayString(values.dayOne)
          : undefined,
      anchorPayDate: values.anchorPayDate
        ? moment(values.anchorPayDate).format("YYYY-MM-DD")
        : moment().format("YYYY-MM-DD"),
    };

    // Call the parent's onSubmit with the expected format
    this.props.onSubmit({
      schedule: scheduleData,
      amount: +this.state.customAmount,
      isDailyCustom: false,
      accountId: this.state.selectedAccountId,
    });
  };

  _getRecommendedAmounts = (frequency: string) => {
    const recommendations = this._getRecommendedContributions(frequency);
    if (!recommendations) return [];

    const contributionLimits = this._getContributionLimits(frequency);
    return filter(
      // original spreads recommendations.other verbatim; `!` is erased at runtime
      [recommendations.recommended, ...recommendations.other!],
      (rec) => {
        return rec! >= contributionLimits.min;
      }
    );
  };

  render() {
    const title = this.state.showConfirmation
      ? "Review scheduled contribution details"
      : "Edit scheduled contribution";

    return (
      <Card className="transfer-card">
        <Card.Body>
          <Card.Title className="transfer-card-title">{title}</Card.Title>
          <Formik
            validateOnChange={false}
            validationSchema={schema}
            onSubmit={(values: FormValues) => {
              if (this.state.showConfirmation) {
                this._handleFinalSubmit(values);
              } else {
                this.setState({ showConfirmation: true });
              }
            }}
            enableReinitialize={true}
            initialValues={this.initialFormValues}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              errors,
              setFieldValue,
            }) => {
              const customAmountValidation = this._setCustomAmount(
                this.state.customAmount || 0,
                values.frequency
              );

              if (this.state.showConfirmation) {
                return this._getConfirmationMessage(values);
              }

              const contributionLimits = this._getContributionLimits(
                values.frequency
              );
              const selectedAccountType = this.props.accounts!.find(
                (a) => a.id === this.state.selectedAccountId
              )?.accountType;
              const toAccountName = (
                accountTypesToEnglishMapping as Record<string, string>
              )[selectedAccountType!];

              return (
                <Form noValidate onSubmit={handleSubmit}>
                  <AccountBox
                    isFrom
                    bankName={this.props.fromBankName}
                    accountName={this.props.fromAccountName}
                    isBank={this.props.isFromBank}
                    lastFourOfAccount={this.props.fromLastFourOfAccount}
                  />
                  <ScrollToFieldError />
                  {size(this.props.accounts) === 1 ? (
                    <AccountBox bankName={toAccountName} />
                  ) : (
                    <Form.Group as={Col} controlId="formGroupToAccount">
                      <Row className="mb-3">
                        <Form.Label>To Account</Form.Label>
                        <Form.Select
                          name="selectedAccountId"
                          value={this.state.selectedAccountId}
                          onChange={(e) => {
                            this.setState({
                              selectedAccountId: e.target.value,
                            });
                            void setFieldValue(
                              "selectedAccountId",
                              e.target.value
                            );
                          }}
                          isInvalid={!!errors.selectedAccountId}
                        >
                          <option value="">Select Account</option>
                          {this.props.accounts!.map((account) => (
                            <option key={account.id} value={account.id}>
                              {
                                (
                                  accountTypesToEnglishMapping as Record<
                                    string,
                                    string
                                  >
                                )[account.accountType!]
                              }
                            </option>
                          ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                          {errors.selectedAccountId}
                        </Form.Control.Feedback>
                      </Row>
                    </Form.Group>
                  )}
                  <Form.Group
                    as={Col}
                    controlId="formGroupFrequency"
                    style={{ paddingTop: 15 }}
                  >
                    <Row className="mb-3">
                      <Form.Label>Frequency</Form.Label>
                      <Form.Select
                        name="frequency"
                        value={values.frequency}
                        onChange={handleChange}
                        isInvalid={!!errors.frequency}
                        isValid={touched.frequency && !errors.frequency}
                      >
                        <option value="">Select Frequency</option>
                        <option value="WEEKLY">Every Week</option>
                        <option value="EVERY_OTHER_WEEK">
                          Every Two Weeks
                        </option>
                        <option value="MONTHLY">Every Month</option>
                      </Form.Select>
                      <Form.Control.Feedback type="invalid">
                        {errors.frequency}
                      </Form.Control.Feedback>
                    </Row>
                  </Form.Group>

                  <Form.Group as={Col}>
                    <Row className="mb-3">
                      <Form.Label className="d-block">
                        {values.frequency === MONTHLY
                          ? "Select your monthly contribution day"
                          : values.frequency
                            ? "Pick a start date for your recurring contribution"
                            : "Select payment timing"}{" "}
                        {values.frequency && (
                          <InfoTooltip
                            tooltipBody="If a transfer falls on a banking holiday or weekend, the transfer will process the following business day."
                            placement="right"
                          />
                        )}
                      </Form.Label>

                      {values.frequency === MONTHLY && (
                        <Form.Select
                          name="dayOne"
                          value={values.dayOne}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={!!errors.dayOne}
                          isValid={touched.dayOne && !errors.dayOne}
                        >
                          <option value="">Select Day of Month</option>
                          {daysOfMonthOptions.map((item) => (
                            <option value={item.value} key={item.key}>
                              {item.value}
                            </option>
                          ))}
                        </Form.Select>
                      )}

                      {values.frequency && values.frequency !== MONTHLY && (
                        <DatePicker
                          className="form-control"
                          name="anchorPayDate"
                          autoComplete="off"
                          placeholderText="Select start date"
                          filterDate={(date) => {
                            const day = date.getDay();
                            const sunday = 0;
                            const saturday = 6;
                            return day !== sunday && day !== saturday;
                          }}
                          minDate={moment().add(1, "days").toDate()}
                          maxDate={moment().add(90, "days").toDate()}
                          // @ts-expect-error react-datepicker's `value` expects string; original passes a Moment (inert), preserved
                          value={values.anchorPayDate}
                          dateFormat="MM/dd/yyyy"
                          selected={
                            (values.anchorPayDate &&
                              // original passes the Moment straight to Date(); cast keeps the runtime value identical
                              new Date(
                                values.anchorPayDate as unknown as string
                              )) ||
                            null
                          }
                          onChange={(val: Date | null) => {
                            void setFieldValue("anchorPayDate", moment(val));
                          }}
                          isInvalid={!!errors.anchorPayDate}
                          isValid={
                            touched.anchorPayDate && !errors.anchorPayDate
                          }
                        />
                      )}

                      {!values.frequency && (
                        <Form.Control
                          disabled
                          placeholder="Select frequency first"
                          style={{ backgroundColor: "#f8f9fa" }}
                        />
                      )}

                      {errors.dayOne && touched.dayOne && (
                        <div
                          className="invalid-feedback"
                          style={{ display: "block" }}
                        >
                          {errors.dayOne}
                        </div>
                      )}
                      {errors.anchorPayDate && touched.anchorPayDate && (
                        <div
                          className="invalid-feedback"
                          style={{ display: "block" }}
                        >
                          {errors.anchorPayDate}
                        </div>
                      )}
                    </Row>
                  </Form.Group>

                  <Form.Group as={Col} controlId="amount">
                    <Form.Label>Amount</Form.Label>
                    <div className="mb-3 position-relative">
                      <span className="currency-sign">$</span>
                      <Form.Control
                        name="amount"
                        placeholder="0.00"
                        value={this.state.customAmount}
                        onChange={(e) => {
                          const amt = parseFloat(e.target.value) || 0;
                          const validation = this._setCustomAmount(
                            amt,
                            values.frequency
                          );

                          void setFieldValue("customAmount", e.target.value);
                          this.setState({
                            customAmount: e.target.value,
                            customAmountError: validation.customAmountError,
                          });
                        }}
                        onBlur={handleBlur}
                        type="number"
                        step="0.01"
                        min="0"
                        disabled={!values.frequency}
                        isInvalid={!!customAmountValidation.customAmountError}
                        isValid={
                          !!this.state.customAmount &&
                          !customAmountValidation.customAmountError
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        {customAmountValidation.customAmountError}
                      </Form.Control.Feedback>

                      {values.frequency && (
                        <Form.Text muted>
                          Amount must be between{" "}
                          <span className="amount-range">
                            {formatCurrency(contributionLimits.min)} -{" "}
                            {formatCurrency(contributionLimits.max)}
                          </span>
                        </Form.Text>
                      )}
                    </div>
                  </Form.Group>

                  <section
                    className="form-sec-2col"
                    style={{ paddingLeft: 10 }}
                  >
                    {this.props.error && (
                      <Alert type="error" msg={this.props.error} />
                    )}

                    <div className="submit-row btn-row">
                      <Button
                        name="cancel"
                        color="cancel"
                        btnLabel="Close"
                        type="button"
                        onClick={this.props.onClose}
                      />
                      <Button
                        label="Continue"
                        // @ts-expect-error original passes Formik's handleSubmit to onClick, omits required `name`, and passes inert `label`; preserved verbatim to keep the rendered DOM unchanged
                        onClick={handleSubmit}
                        loading={this.props.isSubmitting}
                        disabled={
                          !this.state.selectedAccountId ||
                          !values.frequency ||
                          (values.frequency === MONTHLY && !values.dayOne) ||
                          (values.frequency &&
                            values.frequency !== MONTHLY &&
                            !values.anchorPayDate) ||
                          !this.state.customAmount ||
                          this.state.customAmount === "" ||
                          !!customAmountValidation.customAmountError
                        }
                      />
                    </div>
                  </section>
                </Form>
              );
            }}
          </Formik>
        </Card.Body>
      </Card>
    );
  }
}

export default withApollo<Props>(ContributionSetup);
